import { makeLineItem, makeProductVariant } from './data.js';
import { selectPhoto } from './bussiness-logic.js';
import { pageParams } from './page.js';
import { pluck } from './utils-module.js';
import { redirectToPage } from './page-routes.js';
import {
  ecomAddLineItems,
  ecomCheckCollectionTagsAndImages,
  ecomFindAndSetFullUnlockProduct,
  ecomGetAddonCollectionId,
  ecomGetCart,
  ecomGetCollComboVariant,
  ecomGetCollectionOrProductVisibilityForWebapp,
  ecomGetCollectionTagType,
  ecomGetCollectionsWithProductsByEventName,
  ecomGetComboProductsIds,
  ecomIsComboContainsRequiredOption,
  ecomNormalizeProductVariantId,
  normalizeCollectionId,
} from '../../../packages/src/e-commerce-api/EcommerceUtil.js';

// Internal collection row (occoCollectionData element); only the fields this
// module touches are typed, the rest flows through the untyped ecom helpers.
type OccoCollection = {
  id: number;
  external_collection_id?: string | number;
};

// Variant objects flow into makeProductVariant via spread, so the shape must
// cover its destructured parameters; values stay loose — the data comes from
// the untyped ecom helpers and is validated at runtime by makeProductVariant.
type ProductVariant = {
  id: string | number;
  productTitle?: string;
  pid: string | number;
  photo: { id?: string | number; thumb_full_path?: string } | undefined;
  imgUrl: string | undefined;
  productPurpose: string | undefined;
  product_purpose?: string;
  printPool: unknown;
  shippingPrintPool: unknown;
  partialUnlockTags: unknown;
};

type SelectedVariant = {
  id?: string | number;
  variantArr?: ProductVariant[];
};

type Photo = {
  full_path?: string;
};

function readJsonArray<T>(key: string): T[] {
  const parsed: unknown = JSON.parse(localStorage.getItem(key) ?? 'null');
  return Array.isArray(parsed) ? (parsed as T[]) : [];
}

// Opens a specific package (ecommerce collection) directly from the album page,
// skipping combos.html entirely: performs the same preparation the combos page
// does on a package click (mirrors ProductPack.handleComboClick/addToCart in
// combos.js — keep them in sync) and redirects straight to the image-selection
// page (printable/partial-unlock packages) or the shopping cart (digital ones).
//
// Returns true when a redirect was initiated; false means the caller should
// fall back to the package list.
export async function openPackagePurchaseDirectly(collectionId: number | string): Promise<boolean> {
  try {
    const isVip = localStorage.getItem('isVip');
    const allPhotos = readJsonArray<Photo>('allPhotos');
    const selectedPhotos = readJsonArray<Photo>('selectedPhotos');

    const res = await ecomGetCollectionsWithProductsByEventName({
      eventPrivateApiKey: localStorage.getItem('eventPrivateApiKey') || '',
      eventPublicApiKey: localStorage.getItem('eventPublicApiKey') || '',
      eventName: localStorage.getItem('eventName') || '',
    });
    if (res?.status !== 'ok' || !res?.result) return false;
    const collList = res.result.collectionData || [];
    const occoCollList: OccoCollection[] = res.result.occoCollectionData || [];
    if (!Array.isArray(collList) || !Array.isArray(occoCollList) || occoCollList.length === 0) return false;

    // Same page-level setup combos.html performs on load — the cart and
    // select-images pages read these keys later.
    localStorage.setItem('collectionComboIdList', JSON.stringify(ecomGetComboProductsIds(occoCollList)));
    const addonCollectionID = ecomGetAddonCollectionId(occoCollList);
    if (addonCollectionID > 0) localStorage.setItem('addonCollectionID', addonCollectionID);
    for (let c = 0; c < occoCollList.length; c += 1) {
      ecomFindAndSetFullUnlockProduct(occoCollList[c], collList[c]?.collectionData, isVip);
    }

    const combo1 = occoCollList.find((combo) => combo.id === Number(collectionId));
    if (!combo1) return false;

    if (combo1.id === addonCollectionID) {
      // A-La-Carte isn't a normal package — it has its own selection screen
      // (mirrors onClickAddonCollection in combos.js) rather than a
      // combo/cart flow, so it skips the variant preparation below entirely.
      if (!ecomGetCollectionOrProductVisibilityForWebapp(combo1, isVip)) return false;
      redirectToPage.addons();
      return true;
    }

    // hide_from_packages targets stay reachable on purpose — a package can be
    // promoted exclusively through a stock image. Anything else (hidden for
    // this attendee, no matching tagged photos) falls back to the package list.
    const isValidTarget =
      ecomGetCollectionOrProductVisibilityForWebapp(combo1, isVip) &&
      ecomCheckCollectionTagsAndImages(combo1, pluck(allPhotos, 'full_path'));
    if (!isValidTarget) return false;

    const collectionExternalId = normalizeCollectionId(combo1.external_collection_id);
    const combo2 = collList.find((combo) => collectionExternalId === combo.collectionData.id);
    const selectedVariantArr: SelectedVariant = ecomGetCollComboVariant(
      { occoCollectionData: combo1, collectionAndComboData: combo2 },
      isVip
    );
    localStorage.setItem('selectedShopCollectionData', JSON.stringify(selectedVariantArr));
    localStorage.setItem('selectedShopCollectionId', String(combo1.id));

    if ((!selectedPhotos || selectedPhotos.length === 0) && (!allPhotos || allPhotos.length === 0)) {
      redirectToPage.shoppingCart();
      return true;
    }

    const collectionTagType = ecomGetCollectionTagType(combo1);
    localStorage.setItem('collectionAcceptTags', collectionTagType.collectionAcceptTags.join());
    localStorage.setItem('collectionDenyTags', collectionTagType.collectionDenyTags.join());

    if (
      ecomIsComboContainsRequiredOption(combo1, 'printable') ||
      ecomIsComboContainsRequiredOption(combo1, 'partial_unlock_media')
    ) {
      redirectToPage.selectImages();
      return true;
    }

    // Digital-only package: add the line items and land in the cart.
    const enrichedVariantArr = enrichVariantArr({
      variantArr: selectedVariantArr.variantArr || [],
      attendeeUId: pageParams.attendeeUid(),
      eventName: pageParams.eventName(),
      selectedPhotos,
      allPhotos,
    });
    const lineItems = [
      makeLineItem({
        variantId: ecomNormalizeProductVariantId(selectedVariantArr.id),
        variantArr: enrichedVariantArr,
      }),
    ];
    const cart = await ecomGetCart({
      isFromWebapp: true,
      shippingAddress: null,
      eventPrivateApiKey: localStorage.getItem('eventPrivateApiKey') || '',
      eventPublicApiKey: localStorage.getItem('eventPublicApiKey') || '',
    });
    if (!cart?.id) return false;
    await ecomAddLineItems(cart.id, lineItems);
    redirectToPage.shoppingCart();
    return true;
  } catch {
    return false;
  }
}

function isDigitalPack(variant: ProductVariant): boolean {
  return variant.product_purpose === 'full_unlock' || variant.productPurpose === 'full_unlock';
}

function enrichVariantArr({
  variantArr,
  attendeeUId,
  eventName,
  selectedPhotos = [],
  allPhotos = [],
}: {
  variantArr: ProductVariant[];
  attendeeUId: string | null;
  eventName: string | null;
  selectedPhotos?: Photo[];
  allPhotos?: Photo[];
}) {
  const digitalPacks = variantArr
    .filter((variant) => isDigitalPack(variant))
    .map((variant) =>
      makeProductVariant({
        attendeeUId,
        eventName,
        ...variant,
      })
    );
  const others = variantArr
    .filter((variant) => !isDigitalPack(variant))
    .map((variant, i) =>
      makeProductVariant({
        attendeeUId,
        eventName,
        ...variant,
        photo: variant.photo ?? selectPhoto(i, selectedPhotos, allPhotos),
      })
    );
  return others.concat(digitalPacks);
}
